/**
 * result-semantics — client-side mirror of the public API's result read-time
 * semantics (api repo src/dto/mappers.ts toResultDto, PRI-477/PRI-480).
 *
 * The web app still reads the internal /v1/video/:id/result endpoint, which
 * returns the raw job row. The public /v1/analyses mapper derives
 * detected_count + indeterminate_reason at read time; this module applies the
 * SAME rules so the web UI shows identical semantics to what an API customer
 * sees (PRI-488 F2) without any API change:
 *
 *  - explicit inference-side indeterminate_reason is trusted when valid
 *  - answer 'no' with zero confidence and all-zero terms → indeterminate
 *    (model saw nothing; cannot confirm absence)
 *  - indeterminate + nothing to see → nothing_detected, else low_confidence
 *  - nothing_detected forces detected_count to 0 (INV1)
 *  - detected_count is clamped to a non-negative integer
 */

export type IndeterminateReason =
  | 'low_confidence'
  | 'nothing_detected'
  | 'unsupported_query_form'
  | 'duration_mismatch'

const VALID_REASONS: IndeterminateReason[] = [
  'low_confidence',
  'nothing_detected',
  'unsupported_query_form',
  'duration_mismatch',
]

export interface ResultSemantics {
  /** Normalized lowercase answer after read-time overrides. */
  answer: 'yes' | 'no' | 'indeterminate' | null
  /** Present for count-intent queries. 0 = assessable, nothing found. */
  detectedCount: number | null
  /** Present when answer is indeterminate. */
  indeterminateReason: IndeterminateReason | null
}

export function deriveResultSemantics(params: {
  answer: string | null | undefined
  confidence: number | null | undefined
  termConfidences?: Record<string, number> | null
  /** Raw result_json fields from the job row (inference output). */
  resultJson?: Record<string, unknown> | null
}): ResultSemantics {
  const rj = params.resultJson ?? {}
  const rawAnswer = (params.answer ?? '').toLowerCase().trim()
  const answer0: 'yes' | 'no' | 'indeterminate' | null =
    rawAnswer === 'yes' ? 'yes' : rawAnswer === 'no' ? 'no' : rawAnswer === 'indeterminate' ? 'indeterminate' : rawAnswer ? 'indeterminate' : null

  const confidence = typeof params.confidence === 'number' ? params.confidence : 0
  const terms = params.termConfidences ?? (rj.term_confidences as Record<string, number> | undefined) ?? {}
  const allTermsZero = Object.values(terms).every(v => v === 0)

  // PRI-477-D: zero-confidence 'no' is indeterminate.
  const answer: ResultSemantics['answer'] =
    answer0 === 'no' && confidence === 0 && allTermsZero ? 'indeterminate' : answer0

  const rawCount = rj.detected_count
  let detectedCount = typeof rawCount === 'number' && Number.isFinite(rawCount)
    ? Math.max(0, Math.round(rawCount))
    : null

  let indeterminateReason: IndeterminateReason | null = null
  if (answer === 'indeterminate') {
    const explicit = rj.indeterminate_reason
    if (typeof explicit === 'string' && (VALID_REASONS as string[]).includes(explicit)) {
      indeterminateReason = explicit as IndeterminateReason
    } else {
      indeterminateReason = confidence === 0 && allTermsZero ? 'nothing_detected' : 'low_confidence'
    }
  }

  // INV1: nothing_detected wins over detected_count.
  if (indeterminateReason === 'nothing_detected' && detectedCount !== null && detectedCount > 0) {
    detectedCount = 0
  }

  return { answer, detectedCount, indeterminateReason }
}

/**
 * Actionable user-facing copy per indeterminate reason (PRI-488 F2 spec).
 * Replaces the generic "indeterminate" label.
 */
export const INDETERMINATE_COPY: Record<IndeterminateReason, string> = {
  low_confidence: 'Darwin saw something but wasn\u2019t sure \u2014 try a more specific prompt.',
  nothing_detected: 'Nothing found in any frame.',
  unsupported_query_form: 'This prompt can\u2019t be scored \u2014 rephrase as a yes/no or count question.',
  duration_mismatch: 'Result untrusted (video timing mismatch) \u2014 re-upload the video or contact support.',
}

/**
 * One-line answer summary for the result card.
 *  - count query with a determinate answer: "yes — 3 detected"
 *  - determinate without count: "yes"
 *  - indeterminate: the actionable reason copy
 */
export function formatAnswerLine(s: ResultSemantics): string | null {
  if (s.answer == null) return null
  if (s.answer === 'indeterminate') {
    return s.indeterminateReason ? INDETERMINATE_COPY[s.indeterminateReason] : 'Indeterminate.'
  }
  if (s.detectedCount != null) {
    return `${s.answer} \u2014 ${s.detectedCount} detected`
  }
  return s.answer
}
